import { CSSProperties, FC, ReactNode, useCallback, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import { buildMonthCells, dayMarks, dayLoadLevels, peakWeek, COLS, type CalendarEvent } from './calendarModel';
import styles from './Calendar.module.scss';

// Heat bands 0-4; the colours live in Calendar.module.scss ($heat-levels -> .heat1-4 / .heatSq0-4).
const HEAT_LEVELS = 5;
const HEAT_DARK_FROM = 3; // levels >= this get light text (the two darkest bands)

// Type colours come from the `colorOf` prop, so they are data rather than styling: they ride
// into the stylesheet as a custom property instead of a full inline rule.
const dotColor = (color: string): CSSProperties => ({ '--dot-color': color } as CSSProperties);
// Tooltip geometry: kept in sync with .tooltip in the stylesheet.
const TIP_GAP = 6;   // space between the day cell and the tooltip
const TIP_EDGE = 8;  // minimum breathing room from the viewport edge
// Single-letter column headers, Monday-first. moment has no single-letter token, but weekdaysMin
// gives locale-aware short names to take the initial from — so this self-localizes like the month
// and date labels do, instead of showing Swedish M T O T F L S to an English reader.
// weekdaysMin() is always Sunday-indexed regardless of locale, and this grid is Monday-first by
// construction (see mondayIdx in calendarModel), hence the explicit reordering rather than
// weekdaysMin(true), which would put Sunday first for locales like en.
const MONDAY_FIRST = [1, 2, 3, 4, 5, 6, 0];
const weekdayInitials = (): string[] =>
  MONDAY_FIRST.map((i) => moment.weekdaysMin()[i].charAt(0).toUpperCase());

export type CalendarProps = {
  events: CalendarEvent[];
  colorOf: (typeKey: string) => string;
  labelOf: (typeKey: string) => string;
  rangeStart: { year: number; month: number }; // month 0-based
  monthCount: number;                            // 3 (closed) | 12 (modal)
  today?: string;                                // ISO; default now
  selectedDates?: ReadonlySet<string>;            // multi-select highlighted days
  onToggleDate?: (isoDate: string) => void;        // toggle a day in/out of the selection
  onToggleExpand?: () => void;                     // shows the expand/collapse button when set
  expanded?: boolean;                              // drives the button's icon/label
  title?: string;
  subtitle?: string;
  rangeControl?: ReactNode;
  showPeakBadge?: boolean;
  // Receives the ISO week-year too: in a window holding the same week number twice, the number
  // alone does not say which one is the peak.
  peakLabel?: (week: number, isoWeekYear: number) => string;
  heatLabels?: { calm: string; busy: string };
  weekPrefix?: string;                             // week-number prefix, e.g. "v." (sv) / "w" (en)
  holidays?: ReadonlySet<string>;                  // ISO dates rendered as red "röd dag" numbers
  // Accessible name for an interactive day. Without it the cell announces only its day number,
  // which is meaningless in a grid holding twelve 15ths. Supplied by the consumer like every other
  // string here, so this component stays free of hardcoded copy.
  dayLabel?: (isoDate: string, activityCount: number) => string;
  // Accessible name for the expand/collapse button, whose only content is a glyph. Supplied by the
  // consumer like every other string here — without it the button announces as "⤢, button".
  expandLabels?: { expand: string; collapse: string };
};

const Calendar: FC<CalendarProps> = ({
  events, colorOf, labelOf, rangeStart, monthCount,
  today = moment().format('YYYY-MM-DD'),
  selectedDates, onToggleDate, onToggleExpand, expanded = false,
  title, subtitle, rangeControl, showPeakBadge = true, peakLabel, heatLabels, weekPrefix = 'v.',
  holidays, dayLabel, expandLabels,
}) => {
  // The hovered day and its cell ELEMENT — not a one-off measurement of it. The tooltip renders in
  // a portal on document.body rather than inside the cell, so it is not clipped by the grid's
  // scroll container (which is what makes the horizontal scroll safe), and keeping the element lets
  // it be re-measured whenever the page moves underneath it.
  const [hover, setHover] = useState<{ iso: string; el: HTMLElement } | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [tipPos, setTipPos] = useState<
    { left: number; top: number; arrow: number; below: boolean; detached: boolean } | null
  >(null);
  // Ties the tooltip to its trigger for assistive tech. Portaling put it outside the cell's subtree,
  // so nothing associated the two any more — aria-describedby works by id reference regardless of
  // where the element sits. useId keeps two Calendars on one page from colliding.
  const tipId = useId();

  const openTip = (iso: string, el: HTMLElement) => setHover({ iso, el });
  const closeTip = (iso: string | null) => setHover((h) => (h && h.iso === iso ? null : h));

  // Measure the cell and the tooltip, then centre the tooltip on the cell, clamp it inside the
  // viewport (the old CSS centred it blindly, so days in the last columns had their right edge —
  // and the urgency pill — cut off) and flip it above the cell when it would fall off the bottom.
  // The arrow follows the cell rather than the tooltip's centre.
  const place = useCallback(() => {
    const tip = tipRef.current;
    if (!hover || !tip) return;
    // The cell can be torn out from under an open tooltip (see the invalidation effect below);
    // a detached node measures as all-zeros, which would park the tooltip in the corner.
    if (!hover.el.isConnected) { setHover(null); return; }
    const { width, height } = tip.getBoundingClientRect();
    const rect = hover.el.getBoundingClientRect();
    const centre = rect.left + rect.width / 2;
    const left = Math.max(TIP_EDGE, Math.min(centre - width / 2, window.innerWidth - width - TIP_EDGE));
    // Prefer below, flip above when it does not fit, but pick whichever side has more room when it
    // fits in neither — otherwise a tall tooltip on a cell near the top flips above and runs off
    // the screen, which is worse than overflowing downward where the page can still scroll.
    const roomBelow = window.innerHeight - rect.bottom - TIP_GAP - TIP_EDGE;
    const roomAbove = rect.top - TIP_GAP - TIP_EDGE;
    const below = height <= roomBelow || roomBelow >= roomAbove;
    const wanted = below ? rect.bottom + TIP_GAP : rect.top - height - TIP_GAP;
    const top = Math.max(TIP_EDGE, Math.min(wanted, window.innerHeight - height - TIP_EDGE));
    // Once clamped the card no longer sits against its cell, so the arrow would point at empty
    // space. Hide it rather than let it lie about which day this describes.
    const detached = Math.abs(top - wanted) > 0.5;
    const arrow = centre - left;
    setTipPos((prev) => (prev && prev.left === left && prev.top === top && prev.arrow === arrow
      && prev.below === below && prev.detached === detached)
      ? prev : { left, top, arrow, below, detached });
  }, [hover]);

  useLayoutEffect(() => {
    if (!hover) { setTipPos(null); return; }
    place();
  }, [hover, place]);

  // Re-place on scroll and resize rather than closing. Closing looked simpler, but focusing a cell
  // that is off-screen makes the browser scroll it into view, which fired this listener and shut
  // the tooltip in the same frame it opened — so keyboard users could never read a tooltip for any
  // cell that was not already visible. Capture phase so the grid's own horizontal scroll counts.
  useLayoutEffect(() => {
    if (!hover) return undefined;
    window.addEventListener('scroll', place, { capture: true, passive: true });
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, { capture: true });
      window.removeEventListener('resize', place);
    };
  }, [hover, place]);

  // The tooltip is measured once when it opens, but its own size can change afterwards — its day
  // loses a row to a dismissal, a web font finishes loading, a long title rewraps. Any of those
  // leave a flipped-above card positioned from its old height, i.e. detached from its cell.
  // Observing the element covers every cause; watching the event list would only cover the first.
  useLayoutEffect(() => {
    const tip = tipRef.current;
    if (!hover || !tip) return undefined;
    const ro = new ResizeObserver(() => place());
    ro.observe(tip);
    return () => ro.disconnect();
  }, [hover, place]);

  // WCAG 1.4.13: content shown on hover or focus must be dismissible without moving either. Focus
  // stays on the cell, so the tooltip can be reopened by tabbing away and back.
  useLayoutEffect(() => {
    if (!hover) return undefined;
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') setHover(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hover]);

  // Drop the hover when the grid's LAYOUT changes: expanding/collapsing remounts every cell via
  // <table key={monthCount}>, and stepping the range moves them. React fires no mouseleave for a
  // node that was unmounted, and closeTip only matches the cell it was opened from — so without
  // this an open tooltip could outlive its cell and hang over the page, unclickable, until the
  // user happened to hover another event day.
  // Deliberately NOT keyed on `events`: a day that loses its last activity already hides the
  // tooltip through the hoverEvents check at the render site, and depending on the events array's
  // identity would tie the tooltip's existence to how stable the caller's memoisation happens to be.
  useLayoutEffect(() => { setHover(null); }, [monthCount, rangeStart.year, rangeStart.month]);

  const byDate = useMemo(() => {
    const m = new Map<string, CalendarEvent[]>();
    events.forEach((e) => { const a = m.get(e.date); if (a) a.push(e); else m.set(e.date, [e]); });
    return m;
  }, [events]);

  const months = useMemo(() => {
    const out: { year: number; month: number }[] = [];
    for (let i = 0; i < monthCount; i++) {
      const d = moment({ year: rangeStart.year, month: rangeStart.month, day: 1 }).add(i, 'months');
      out.push({ year: d.year(), month: d.month() });
    }
    return out;
  }, [rangeStart.year, rangeStart.month, monthCount]);

  // Recomputed per render rather than hoisted: moment's locale is global and switches with the
  // app's language, which is not something this component can list as a dependency.
  const weekdays = weekdayInitials();

  const peak = useMemo(() => (showPeakBadge ? peakWeek(events) : null), [events, showPeakBadge]);
  const dayLevels = useMemo(() => dayLoadLevels(events), [events]);

  const renderCell = (iso: string | null, dayNo: number, outside: boolean, col: number, week: number) => {
    const dayEvents = iso ? (byDate.get(iso) ?? []) : [];
    // Widest form is bounded to fit the narrowest cell the grid guarantees — see dayMarks.
    const { dots, more } = dayMarks(dayEvents.map((e) => e.typeKey));
    const level = iso ? (dayLevels.get(iso) ?? 0) : 0;
    const isToday = iso === today;
    const isSel = iso != null && !!selectedDates?.has(iso);
    const isOverdue = !outside && dayEvents.some((e) => e.badgeTone === 'danger');
    const isHoliday = iso != null && !!holidays?.has(iso);
    const cls = [
      styles.dinner,
      level > 0 ? styles[`heat${level}`] : '',
      dayEvents.length ? styles.hasEv : '',
      level >= HEAT_DARK_FROM ? styles.onDark : '',
      isOverdue ? styles.overdue : '',
      isToday ? styles.today : '',
      // Only ring a day that is actually interactive. Every handler below is gated on dayEvents,
      // so ringing an event-less day would advertise a click target that does nothing.
      isSel && dayEvents.length ? styles.selected : '',
    ].join(' ');
    return (
      <td key={col} className={`${styles.day} ${col % 7 >= 5 ? styles.wkndCol : ''}`}>
        <div
          className={cls}
          role={dayEvents.length ? 'button' : undefined}
          tabIndex={dayEvents.length ? 0 : undefined}
          // Both only apply to days that are actually interactive: a day with no activities has no
          // role, and aria-label/aria-pressed on a role-less div is at best ignored, at worst noise.
          aria-label={dayEvents.length && iso ? dayLabel?.(iso, dayEvents.length) : undefined}
          aria-pressed={dayEvents.length ? isSel : undefined}
          // Only while this cell's tooltip is actually rendered — a dangling reference to a
          // non-existent id would just be dropped, but pointing at another day's card would not.
          aria-describedby={hover?.iso === iso ? tipId : undefined}
          onMouseEnter={(ev) => { if (iso && dayEvents.length) openTip(iso, ev.currentTarget); }}
          onMouseLeave={() => closeTip(iso)}
          onFocus={(ev) => { if (iso && dayEvents.length) openTip(iso, ev.currentTarget); }}
          onBlur={() => closeTip(iso)}
          onClick={() => iso && dayEvents.length && onToggleDate?.(iso)}
          onKeyDown={(ev) => { if (iso && dayEvents.length && (ev.key === 'Enter' || ev.key === ' ')) { ev.preventDefault(); onToggleDate?.(iso); } }}
        >
          <span className={`${styles.weekNo}${outside ? ` ${styles.weekNoOut}` : ''}`} aria-hidden="true">{col % 7 === 0 ? `${weekPrefix}${week}` : ''}</span>
          <span className={`${styles.dnum} ${outside ? styles.out : ''} ${isHoliday && !outside ? styles.holiday : ''}`}>{dayNo}</span>
          <div className={styles.marks}>
            {dots.map((tk, i) => (
              <span key={i} className={styles.dot} style={dotColor(colorOf(tk))} />
            ))}
            {more > 0 && <span className={styles.more}>+{more}</span>}
          </div>
        </div>
      </td>
    );
  };

  // Rendered on document.body, so it escapes both the grid's scroll container and the page's
  // overflow-x:hidden. It is inert (pointer-events:none in the stylesheet): it follows the cell
  // under the cursor and never becomes a hover target itself.
  const hoverEvents = hover ? (byDate.get(hover.iso) ?? []) : [];
  const tooltip = hover && hoverEvents.length > 0 && createPortal(
    <div
      ref={tipRef}
      id={tipId}
      className={[
        styles.tooltip,
        tipPos && !tipPos.below ? styles.tooltipAbove : '',
        tipPos?.detached ? styles.tooltipDetached : '',
      ].join(' ')}
      role="tooltip"
      style={{
        left: tipPos ? `${tipPos.left}px` : 0,
        top: tipPos ? `${tipPos.top}px` : 0,
        // Hidden for the first paint only, while its real height is measured.
        visibility: tipPos ? 'visible' : 'hidden',
        ['--arrow-x' as string]: tipPos ? `${tipPos.arrow}px` : '50%',
      } as CSSProperties}
    >
      <div className={styles.ttDate}>{moment(hover.iso, 'YYYY-MM-DD').format('dddd D MMMM')}</div>
      {hoverEvents.map((e) => (
        <div key={e.id} className={styles.ttRow}>
          <span className={styles.sw} style={dotColor(colorOf(e.typeKey))} />
          <div className={styles.ttTxt}>
            <div className={styles.ttType}>{labelOf(e.typeKey)}</div>
            <div className={styles.ttSub}>{e.title}</div>
          </div>
          {e.badge && <span className={`${styles.pill} ${styles[e.badgeTone ?? 'muted']}`}>{e.badge}</span>}
        </div>
      ))}
    </div>,
    document.body,
  );

  return (
    <div className={styles.cal}>
      <div className={styles.head}>
        <div>
          {title && <div className={styles.title}>{title}</div>}
          {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
        </div>
        <div className={styles.ctrls}>
          {peak && peakLabel && <span className={styles.peakTag}>{peakLabel(peak.week, peak.year)}</span>}
          {rangeControl}
          {onToggleExpand && (
            <button
              type="button"
              className={styles.maxBtn}
              onClick={onToggleExpand}
              aria-label={expanded ? expandLabels?.collapse : expandLabels?.expand}
              aria-expanded={expanded}
            >
              {expanded ? '⤡' : '⤢'}
            </button>
          )}
        </div>
      </div>
      <div className={styles.gridWrap}>
        {/* key on monthCount so the grid fades in only on expand/collapse, not on range steps */}
        <table key={monthCount} className={`${styles.cg} ${styles.gridAnim}`}>
          <thead>
            <tr>
              <th className={styles.mlabel} />
              {Array.from({ length: COLS }, (_, c) => (
                <th key={c} scope="col" className={c % 7 >= 5 ? styles.wknd : ''}>{weekdays[c % 7]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {months.map(({ year, month }) => (
              <tr key={`${year}-${month}`}>
                {/* th, not td: it labels the row, so table navigation can announce which month a
                    day cell belongs to instead of leaving every row indistinguishable. */}
                <th scope="row" className={styles.mlabel}>{moment({ year, month, day: 1 }).format('MMM').toUpperCase()}</th>
                {buildMonthCells(year, month).map((cell) => renderCell(cell.isoDate, cell.dayNo, cell.outside, cell.col, cell.week))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {heatLabels && events.length > 0 && (
        <div className={styles.heatKey}>
          {heatLabels.calm}
          <span className={styles.heatScale}>
            {Array.from({ length: HEAT_LEVELS }, (_, i) => (
              <span key={i} className={`${styles.heatSq} ${styles[`heatSq${i}`]}`} />
            ))}
          </span>
          {heatLabels.busy}
        </div>
      )}
      {tooltip}
    </div>
  );
};

export default Calendar;
